import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import AdminGuard from "@/components/AdminGuard";
import { FeatureFlagGuard } from "@/components/FeatureFlagGuard";
import { ActivityRouteGuard } from "@/components/ActivityRouteGuard";
import AdminLayout from "@/components/AdminLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Activities from "@/pages/Activities";
import ActivityEditor from "@/pages/ActivityEditor";
import Paths from "@/pages/Paths";
import PathEditor from "@/pages/PathEditor";
import AdminSettings from "@/pages/AdminSettings";
import AdminProfile from "@/pages/AdminProfile";
import ResetPassword from "@/pages/ResetPassword";
import MobileResetPassword from "@/pages/MobileResetPassword";
import ApiDoc from "@/pages/ApiDoc";
import Simulator from "@/pages/Simulator";
import Users from "@/pages/Users";
import LegalPage from "@/pages/LegalPage";
import Help from "@/pages/Help";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <SentryRoutes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/mobile-reset-password" element={<MobileResetPassword />} />
            <Route path="/api/docs" element={<ApiDoc />} />
            <Route path="/legal/:type" element={<LegalPage />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              element={
                <AdminGuard>
                  <AdminLayout />
                </AdminGuard>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/activities" element={<FeatureFlagGuard flagKey="feature_standalone_activities_enabled"><Activities /></FeatureFlagGuard>} />
              <Route path="/activities/new" element={<ActivityRouteGuard><ActivityEditor /></ActivityRouteGuard>} />
              <Route path="/activities/:id" element={<ActivityRouteGuard><ActivityEditor /></ActivityRouteGuard>} />
              <Route path="/paths" element={<Paths />} />
              <Route path="/paths/new" element={<PathEditor />} />
              <Route path="/paths/:id" element={<PathEditor />} />
              <Route path="/settings" element={<AdminSettings />} />
              <Route path="/users" element={<Users />} />
              <Route path="/profile" element={<AdminProfile />} />
              <Route path="/simulator" element={<Simulator />} />
              <Route path="/help" element={<Help />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </SentryRoutes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
